import { useContext, useEffect, useState, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import Typography, { TypographyProps } from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';

import setDiffsHelper from '#utils/set-diffs-helper';
import makeid from '#utils/make-id';
import {
  DATE_FORMAT_STRING,
  formatDate,
  formatCurrency,
} from '#common/utils';
import URLSearchParamsEx from '#utils/url-ex';
import {
  FULFILLMENT_DATA,
  FULFILLMENT_STATUS,
} from '#common/constants';
import {
  useGetBulkOrderMutation,
  useUpdateBulkOrderMutation,
  useReceiveAllBarcodeItemsMutation,
} from '#services/order';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import EnhancedTable, { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';
import OrderItemDetails, { OrderItemDetailsItem, OrderItemDetailsOrder } from '#components/OrderItemDetails';
import TitleBar, { TitleBarItemTraveler } from '#components/TitleBar';
import type { Diff } from '#utils/order-diff-utils';

interface BulkOrderItem {
  id?: string | number;
  items?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

interface BulkOrderShape {
  id?: string | number;
  _id?: string | number;
  orderStatus?: number;
  orders: BulkOrderItem[];
  barcodeItems: Array<{ barcode: string; received?: boolean; [key: string]: unknown }>;
  items?: Array<Record<string, unknown>>;
  total?: number;
  bulkOrderIndex?: number;
  availableDate?: string | number | Date | null;
  createdDate?: string;
  createdByUserName?: string;
  lastUpdatedBy?: string;
  notes?: string;
  [key: string]: unknown;
}

interface DraftItem {
  _id?: unknown;
  data?: Record<string, unknown>;
}

type MutationResult = { error?: unknown; data?: BulkOrderShape };

export default function AdminBulkDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    showBackdrop,
    hideBackdrop,

    showDialog,
    hideDialog,
    setDialogData,

    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const [bulkOrder, setBulkOrder] = useState<BulkOrderShape | undefined>();

  const [getBulkOrder] = useGetBulkOrderMutation();
  const [updateBulkOrder] = useUpdateBulkOrderMutation();
  const [receiveAllBarcodeItems] = useReceiveAllBarcodeItemsMutation();
  const [diffs, setDiffs] = useState<Diff[]>([]);
  const [selected, setSelected] = useState<Array<string | number | undefined>>([]);
  const draftItem = useSelector(
    (state: { draftItem: { item: DraftItem | null } }) => state.draftItem.item,
  );

  const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);

  useEffect(() => {
    showBackdrop();
    void getBulkOrder(urlParams.values)
      .then((res) => {
        const result = res as MutationResult;
        hideBackdrop();
        if (result.error || !result.data) {
          return;
        }

        const newOrder: BulkOrderShape = { ...result.data };
        if (draftItem && result.data._id === draftItem._id) {
          const newItem = { ...(draftItem.data || {}) };
          newItem._id = `unsaved_${makeid(5)}`;
          setDiffs([...diffs, {
            event: 'add',
            scope: 'items',
            data: newItem,
          }]);

          newOrder.items = [...(newOrder.items || []), newItem];
        }

        setBulkOrder(newOrder);
      });
  }, [location.key]);

  if (!bulkOrder) {
    return null;
  }

  const cancelled = (bulkOrder.orderStatus === FULFILLMENT_STATUS.CANCELED);

  const totalItemsCount = bulkOrder.orders.reduce((sum, order) => {
    const orderItemsSum = (order.items || []).reduce(
      (itemSum, item) => itemSum + (Number(item.amounts) || 0),
      0,
    );
    return sum + orderItemsSum;
  }, 0);

  const orderProps = {
    rows: bulkOrder.orders,
  };

  const receivingProps = {
    rows: bulkOrder.barcodeItems.map((bc) => ({
      ...bc,
      id: bc.barcode,
    })),

    hasSelectColumn: !cancelled,
    canSelect: (row?: EnhancedTableRow) => !row?.received,
    canSelectAll: (rows: EnhancedTableRow[]) => rows.some((r) => !r.received),
    selected,
    setSelected,
  };

  const handleTravelBulkOrder = (direction: number) => {
    urlParams.set('bulkOrderIndex', (val) => String(Number(val) + direction));
    const search = urlParams.toString();
    void navigate({
      pathname: '/management/bulk/detail',
      search,
    });
  };

  const handleSaveOrders = () => {
    showBackdrop();

    void updateBulkOrder({
      bulkOrderId: bulkOrder.id,
      diffs,
    }).then((res) => {
      const result = res as MutationResult;
      const hasError = simpleSnackbar(result as unknown as Parameters<typeof simpleSnackbar>[0], 'Bulk order has been saved.');
      if (hasError) {
        return;
      }

      const newBulkOrder: BulkOrderShape = {
        bulkOrderIndex: bulkOrder.bulkOrderIndex,
        total: bulkOrder.total,
        ...result.data,
      } as BulkOrderShape;
      setBulkOrder(newBulkOrder);
      setDiffs([]);
    }).finally(() => {
      hideBackdrop();
    });
  };

  const handleBatchReceive = () => {
    if (!selected.length) {
      return;
    }

    setDialogData({
      title: 'Confirmation',
      text: `Receive selected ${selected.length} items into receiving list?`,
      agree: 'Yes',
      disagree: 'No',
      onDisagree: () => hideDialog(),
      onAgree: () => {
        showBackdrop();

        void receiveAllBarcodeItems({
          bulkOrderId: bulkOrder.id,
          barcodes: selected,
        }).then((res) => {
          const result = res as MutationResult;
          hideDialog();

          const hasError = simpleSnackbar(result as unknown as Parameters<typeof simpleSnackbar>[0], 'Selected barcode items have been received.');
          if (hasError) {
            return;
          }

          const newBulkOrder: BulkOrderShape = {
            bulkOrderIndex: bulkOrder.bulkOrderIndex,
            total: bulkOrder.total,
            ...result.data,
          } as BulkOrderShape;
          setBulkOrder(newBulkOrder);
          setSelected([]);
        }).finally(() => {
          hideBackdrop();
        });
      },
    });
    showDialog();
  };

  const orderHeaders: EnhancedTableProps['headers'] = [{
    id: 'id',
    label: 'Customer Order #',
    renderer: (row) => (
      <Link component="button" onClick={() => { void navigate(`/management/order/detail?orderId=${row.id}`); }}>
        {row.id as ReactNode}
      </Link>
    ),
  }, {
    id: '',
    label: 'Customer',
    renderer: (item) => item.createdAsName as ReactNode,
  }, {
    id: 'createdDate',
    label: 'Order Date',
    renderer: (item) => formatDate(item.createdDate as Parameters<typeof formatDate>[0]),
  }, {
    id: 'items',
    label: 'Items',
    renderer: (item) => (item.items as Array<{ amounts?: number }> || []).reduce(
      (sum, itm) => sum + (Number(itm.amounts) || 0),
      0,
    ),
  }, {
    id: 'totalPrice',
    label: 'Total',
    renderer: (item) => `${formatCurrency(item.totalPrice as Parameters<typeof formatCurrency>[0])}`,
  }];

  const receivingHeaders: EnhancedTableProps['headers'] = [{
    id: 'barcode',
    label: 'Barcode',
    renderer: (row) => row.barcode as ReactNode,
  }, {
    id: '',
    label: 'Item',
    renderer: (row) => {
      const order = bulkOrder.orders.find((o) => o.id === row.orderId);
      const item = (order?.items || []).find(
        (i) => (i as { id?: unknown }).id === row.itemId,
      );
      return (
        <OrderItemDetails
          item={item as unknown as OrderItemDetailsItem}
          order={order as unknown as OrderItemDetailsOrder}
        />
      );
    },
  }, {
    id: 'orderId',
    label: 'Customer Order #',
    renderer: (item) => (
      <Link component="button" onClick={() => { void navigate(`/management/order/detail?orderId=${String(item.orderId)}`); }}>
        {item.orderId as ReactNode}
      </Link>
    ),
  }, {
    id: 'status',
    label: 'Status',
    renderer: (item) => {
      const availableDate = item.availableDate ? new Date(item.availableDate as string) : null;
      if (item.archived) {
        return (<Chip label="Pulled out" color="error" />);
      }
      if (item.received) {
        return (<Chip label="Received" color="success" />);
      }
      if (!availableDate) {
        return (<Chip label="Pending" color="warning" />);
      }
      if (availableDate < new Date()) {
        return (<Chip label="Past Due" color="error" />);
      }
      return (<Chip label="In Production" color="info" />);
    },
  }];

  return (
    <>
      <TitleBar
        title="Bulk Order Detail"
        hideActionButtons={!urlParams.values.bulkOrderIndex}
      >
        <TitleBarItemTraveler
          onTravelItem={(direction) => handleTravelBulkOrder(direction)}
          itemIndex={bulkOrder.bulkOrderIndex as number}
          itemSize={bulkOrder.total as number}
        />
      </TitleBar>
      <Box sx={{
        mb: 1,
        display: 'flex',
        justifyContent: 'space-between',
      }}
      >
        <Box>
          Bulk Order #
          {bulkOrder.id}
          {' '}
          (
          {totalItemsCount}
          {' '}
          {totalItemsCount === 1 ? 'item' : 'items'}
          )
        </Box>

        <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
          <Select
            size="small"
            value={bulkOrder.orderStatus}
            disabled={cancelled && !diffs.length}
            onChange={(e) => {
              const newValue = e.target.value;
              setDiffsHelper('orderStatus', newValue, diffs, setDiffs);
              setBulkOrder({
                ...bulkOrder,
                orderStatus: newValue,
              });
            }}
          >
            {FULFILLMENT_DATA.map((fst) => (
              <MenuItem key={fst.id} value={fst.id}>{fst.text}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <EnhancedTable
        name="order-list"
        headers={orderHeaders}
        {...orderProps}
      />
      <Box
        sx={{
          py: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
            borderRadius: 1,
          }}
        >
          <Typography
            variant={'h7' as TypographyProps['variant']}
            gutterBottom
            sx={{
              display: 'block',
            }}
          >
            Receiving List
          </Typography>
          <Button
            variant="outlined"
            disabled={!selected.length}
            onClick={handleBatchReceive}
          >
            Receive
          </Button>
        </Box>

        <EnhancedTable
          name="receiving-list"
          headers={receivingHeaders}
          {...receivingProps}
        />
      </Box>
      <Box
        sx={{
          py: 3,
        }}
      >
        <Typography
          variant={'h7' as TypographyProps['variant']}
          gutterBottom
          sx={{
            display: 'block',
          }}
        >
          Available Date
        </Typography>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
        >
          <DesktopDatePicker
            format={DATE_FORMAT_STRING}
            name="availableDate"
            disabled={cancelled}
            value={
              (bulkOrder.availableDate === null || bulkOrder.availableDate === undefined)
                ? null
                : new Date(bulkOrder.availableDate)
            }
            onChange={(d) => {
              setDiffsHelper('availableDate', d, diffs, setDiffs);
              setBulkOrder({
                ...bulkOrder,
                availableDate: d,
              });
            }}
            sx={{ minWidth: 350 }}
            slotProps={{ textField: { size: 'small' } }}
          />
        </LocalizationProvider>
      </Box>
      <Paper
        sx={{
          px: 2,
          mt: 2,
          '& .MuiGrid-root': { mb: 2 },
        }}
      >
        <Box sx={{ typography: 'subtitle2' }}>
          <Grid container>
            <Grid size={4}>
              Created Date
            </Grid>
            <Grid size={8}>
              {formatDate(bulkOrder.createdDate as string)}
            </Grid>

            <Grid size={4}>
              Placed By
            </Grid>
            <Grid size={8}>
              {bulkOrder.createdByUserName || '-'}
            </Grid>

            <Grid size={4}>
              Last Updated By
            </Grid>
            <Grid size={8}>
              {bulkOrder.lastUpdatedBy || '-'}
            </Grid>
          </Grid>
        </Box>
      </Paper>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Typography variant={'h7' as TypographyProps['variant']} gutterBottom>
          Notes
        </Typography>
      </Box>
      <Box>
        <TextField
          multiline
          fullWidth
          rows={5}
          value={bulkOrder.notes}
          disabled={cancelled}
          onChange={(event) => {
            const newValue = event.target.value;
            setDiffsHelper('notes', newValue, diffs, setDiffs);
            setBulkOrder({
              ...bulkOrder,
              notes: newValue,
            });
          }}
        />
      </Box>
      <Box>
        <Button
          variant="contained"
          onClick={handleSaveOrders}
        >
          Save
        </Button>
      </Box>
    </>
  );
}
